import useHttp from "../../../../hooks/use-http";
import convertToExcel from "../../../../components/ExportToExcel";
import Pagination from "../../../../components/Pagination";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const LabourDetailsReport = () => {
    const navigate = useNavigate();
    const frmRef = useRef({});

    const handleBack = () => navigate("/operationsReports");

    const { sendRequest: getData } = useHttp();
    const { sendRequest: getCropVariety } = useHttp();
    const { sendRequest: getNurseries } = useHttp();

    const [data, setData] = useState([]);

    // Dropdowns
    const [nurseries, setNurseries] = useState([]);
    const [cropList, setCropList] = useState([]);
    const [allVarietyList, setAllVarietyList] = useState([]);
    const [varietyList, setVarietyList] = useState([]);

    // Filters
    const [nurseryId, setNurseryId] = useState("");
    const [cropId, setCropId] = useState("");
    const [varietyId, setVarietyId] = useState("");

    // Date Filters
    const currentDate = new Date().toISOString().slice(0, 10);
    const d = new Date();
    d.setDate(d.getDate() - 90);

    const [fromDate, setFromDate] = useState(d.toISOString().slice(0, 10));
    const [toDate, setToDate] = useState(currentDate);
    const [showDateFilter, setShowDateFilter] = useState(false);

    // Pagination
    const [itemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const paginate = (page) => setCurrentPage(page);

    const indexLast = currentPage * itemsPerPage;
    const indexFirst = indexLast - itemsPerPage;
    const currentItems = data.slice(indexFirst, indexLast);

    // ================= MASTER DATA LOAD =================
    useEffect(() => {
        getNurseries(
            { url: `/api/warehouses/?orgId=${localStorage.getItem("orgId")}` },
            (res) => setNurseries(res || [])
        );
    }, []);

    useEffect(() => {
        getCropVariety(
            { url: `/api/farmerCropVarietyForSelection/?orgId=${localStorage.getItem("orgId")}` },
            (res) => {
                setCropList(res?.cropList || []);
                setAllVarietyList(res?.varietylist || []);
                setVarietyList(res?.varietylist || []);
            }
        );
    }, []);

    const handleCropChange = (id) => {
        setCropId(id);
        setVarietyId("");

        if (id) {
            setVarietyList(allVarietyList.filter(v => String(v.cropMasterId) === String(id)));
        } else {
            setVarietyList(allVarietyList);
        }
    };

    // ================= REPORT FETCH =================
    const fillData = () => {
        let url = `/api/labourdetailsReport/?orgId=${localStorage.getItem("orgId")}`;

        if (showDateFilter) url += `&fromDate=${fromDate}&toDate=${toDate}`;
        if (nurseryId) url += `&nurseryId=${nurseryId}`;
        if (cropId) url += `&cropId=${cropId}`;
        if (varietyId) url += `&varietyId=${varietyId}`;

        getData({ url }, (res) => setData(res || []));
    };

    useEffect(() => fillData(), []);

    // ================= EXCEL EXPORT =================
    const fieldsToExport = [
        "date",
        "docNo",
        "executiveName",
        "nurseryName",
        "nurserymoduleName",
        "cropName",
        "varietyName",
        "labourType",
        "labourQty",
        "hours",
        "labourAmount",
        "labourName",
    ];

    const customHeaders = [
        "Date",
        "Doc No",
        "Executive Name",
        "Nursery Name",
        "Module Name",
        "Crop Name",
        "Variety Name",
        "Labour Type",
        "Qty",
        "Hours",
        "Cost",
        "Labour Name",
    ];

    const formatNumber = (num) => {
        if (num === null || num === undefined || num === "") return "0.00";
        return Number(num).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const handleExportExcel = () => {
        const filename = "OverallLabourReport.xlsx";

        const formattedData = data.map((row) => ({
            date: row.date,
            docNo: row.docNo,
            executiveName: row.executiveName || "",
            nurseryName: row.nurseryName || "",
            nurserymoduleName: row.nurserymoduleName || "",
            cropName: row.cropName || "",
            varietyName: row.varietyName || "",
            labourType: row.labourDetails?.labourType || "",
            labourQty: row.labourDetails?.labourQty || "",
            hours: row.labourDetails?.hours || "",
            labourAmount: formatNumber(row.labourDetails?.labourAmount),
            labourName: row.labourDetails?.labourName || "",
        }));

        convertToExcel(formattedData, fieldsToExport, filename, customHeaders);
    };

    return (
        <div className="content-wrapper">

            {/* HEADER */}
            <div className="row mx-2 mt-3">
                <div className="col-md-8">
                    <label className="card-title mx-3">Overall Labour Consumption Report</label>
                </div>
                <div className="col-md-4 text-right mt-3">
                    <button className="btn btn-tool" onClick={handleBack}>
                        <i className="fas fa-times" />
                    </button>
                </div>
            </div>

            {/* FILTERS */}
            <section className="content">
                <div className="container-fluid">
                    <div className="row">

                        {/* Nursery */}
                        <div className="col-md-3">
                            <label>Nursery Name</label>
                            <select
                                className="form-control"
                                value={nurseryId}
                                onChange={(e) => setNurseryId(e.target.value)}
                            >
                                <option value="">Select</option>
                                {nurseries.map(n => (
                                    <option key={n.id} value={n.id}>{n.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Crop */}
                        <div className="col-md-3">
                            <label>Crop Name</label>
                            <select
                                className="form-control"
                                value={cropId}
                                onChange={(e) => handleCropChange(e.target.value)}
                            >
                                <option value="">Select</option>
                                {cropList.map(c => (
                                    <option key={c.cropMasterId} value={c.cropMasterId}>{c.cropName}</option>
                                ))}
                            </select>
                        </div>

                        {/* Variety */}
                        <div className="col-md-3">
                            <label>Variety Name</label>
                            <select
                                className="form-control"
                                value={varietyId}
                                onChange={(e) => setVarietyId(e.target.value)}
                            >
                                <option value="">Select</option>
                                {varietyList.map(v => (
                                    <option key={v.varietyId} value={v.varietyId}>{v.varietyName}</option>
                                ))}
                            </select>
                        </div>

                        {/* Date Filter */}
                        <div className="col-md-3">
                            <label>Apply Date Filter</label>
                            <select
                                className="form-control"
                                ref={(el) => (frmRef.current["date_filter"] = el)}
                                onChange={(e) => setShowDateFilter(e.target.value === "Yes")}
                            >
                                <option value="">Select</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                            </select>
                        </div>

                        {showDateFilter && (
                            <>
                                <div className="col-md-2">
                                    <label>From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        max={currentDate}
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                    />
                                </div>

                                <div className="col-md-2">
                                    <label>To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        max={currentDate}
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                </div>
                            </>
                        )}

                        {/* Submit */}
                        <div className="col-md-2 text-right">
                            <label>&nbsp;</label>
                            <button className="form-control btn btn-primary" onClick={fillData}>
                                Submit
                            </button>
                        </div>

                    </div>
                </div>

                <h6>Please select the parameters to get the desired result</h6>
                <hr />
            </section>

            {/* EXPORT + TABLE */}
            <section className="content">
                <div className="container-fluid">

                    <div className="text-right my-2">
                        <button type="button" className="btn btn-sm btn-warning" onClick={handleExportExcel}>
                            <i className="fas fa-file-excel"></i>
                        </button>
                    </div>

                    <div className="card">
                        <div className="card-body">

                            <table className="table table-bordered table-sm text-center">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Doc No</th>
                                        <th>Executive Name</th>
                                        <th>Nursery Name</th>
                                        <th>Module Name</th>
                                        <th>Crop Name</th>
                                        <th>Variety Name</th>
                                        <th>Labour Type</th>
                                        <th>Labour Qty</th>
                                        <th>Work Hours</th>
                                        <th>Labour Cost</th>
                                        <th>Labour Name</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {currentItems.length ? currentItems.map((x, i) => (
                                        <tr key={i}>
                                            <td>{x.date}</td>
                                            <td>{x.docNo}</td>
                                            <td>{x.executiveName || "-"}</td>
                                            <td>{x.nurseryName || "-"}</td>
                                            <td>{x.nurserymoduleName || "-"}</td>
                                            <td>{x.cropName || "-"}</td>
                                            <td>{x.varietyName || "-"}</td>
                                            <td>{x.labourDetails?.labourType || "-"}</td>
                                            <td>{x.labourDetails?.labourQty || "-"}</td>
                                            <td>{x.labourDetails?.workhours || "-"}</td>
                                            <td>{x.labourDetails?.labourAmount || "-"}</td>
                                            <td>{x.labourDetails?.labourName || "-"}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="12">No Records Found</td></tr>
                                    )}
                                </tbody>
                            </table>

                        </div>

                        <div className="card-footer clearfix">
                            <Pagination
                                totalItems={data.length}
                                itemsPerPage={itemsPerPage}
                                onPageChange={paginate}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                            />
                        </div>

                    </div>

                </div>
            </section>

        </div>
    );
};

export default LabourDetailsReport;
